/*
  <wtp-free-shipping-bar>: the free shipping progress bar, in UpCart's cart drawer and on the
  cart page.

  UpCart draws the drawer, so the bar gets in through UpCart's Custom HTML (Cart Editor ›
  Settings › Custom HTML › "Below header/announcements/rewards") as the bare element,
  <wtp-free-shipping-bar></wtp-free-shipping-bar>. Where this script isn't loaded the element
  is unknown and shows nothing. The cart page renders it with snippets/free-shipping-bar.liquid,
  which also passes the cart the page was built with. See docs/free-shipping-sync.md.

  The look is design 07 ("Milestone") of WK-59: a line saying how much more unlocks free
  shipping, a rail that fills up to a dot for the cart and ends in a van for the goal, and the
  cart total and the goal under it. Once the cart reaches the figure, the rail is full and the
  van turns into a tick. Its words are the cart.free_shipping_bar translations, passed in
  theme.settings.cart_free_shipping_bar by layout/theme.liquid.

  The maths are those of the theme's previous bar (updateFreeShipping() in global.js):
  - The figure is theme.settings.cart_free_shipping_threshold, in the visitor's own currency
    (snippets/free-shipping-threshold.liquid). 0 means there is none, and then there's no bar.
  - The spend is the cart's total_price: after discounts and before taxes, which is what
    Shopify checks free shipping rates against. UpCart's cart events carry the same number as
    cart.total.
  - Nothing shows while the cart is empty, or when Theme settings › Cart › "Enable shipping
    notification" is off.

  UpCart renders the drawer in a shadow root and puts Custom HTML in as markup, which it
  rebuilds now and then: whenever the cart stops being empty, and on every total change with
  "Force re-render of custom HTML fields". So the bar keeps its styles in its own shadow root,
  and draws itself whenever it's inserted and whenever UpCart reports the cart.
*/
(() => {
  if (customElements.get('wtp-free-shipping-bar')) return;

  // Stroke icons in the theme's style: 1.5px lines, square ends.
  const icon = (name, paths) =>
    `<svg class="${name}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="square" aria-hidden="true" focusable="false">${paths}</svg>`;
  const VAN = icon(
    'van',
    '<path d="M2.5 6.5h11v9h-11z"/><path d="M13.5 9.5h4l3 3.2v2.8h-7"/><circle cx="6.5" cy="17" r="1.8"/><circle cx="17" cy="17" r="1.8"/>',
  );
  const TICK = icon('tick', '<path d="M5 12.5l4.2 4.2L19 7" stroke-width="2.5"/>');

  // The theme's own variables (custom properties reach into shadow roots); the fallbacks are their
  // values today. The padding lines the bar up with UpCart's line items; a rule for
  // wtp-free-shipping-bar outside (UpCart's Custom CSS, assets/component-cart.css) overrides it.
  // --free-shipping-bar-background is the colour behind the bar, for the dot's ring and the goal.
  const STYLES = `
    :host {
      display: block;
      padding: 1.8rem 2.8rem 0;
      --ink: rgb(var(--color-heading-text, 0, 0, 0));
      --line: rgb(var(--color-progress-bar, 21, 21, 21));
      --paper: var(--free-shipping-bar-background, #fff);
    }

    :host([hidden]) {
      display: none;
    }

    .bar {
      --progress: 0%;
      color: var(--ink);
      font-family: var(--font-body-family, sans-serif);
      font-style: normal;
      font-weight: 400;
      letter-spacing: normal;
      text-align: start;
      text-transform: none;
    }

    p {
      margin: 0;
    }

    .message {
      font-size: 1.4rem;
      line-height: 1.35;
    }

    /* The theme has Untitled Sans in Regular only: weight is a hairline stroke. */
    b {
      font-weight: 400;
      -webkit-text-stroke: 0.035em currentColor;
      letter-spacing: 0.01em;
    }

    /* The rail, centred in a track as tall as the goal's circle. */
    .track {
      position: relative;
      margin-block-start: 0.6rem;
      padding-block: 1rem;
    }

    .rail {
      position: relative;
      height: 0.4rem;
      margin-inline-end: 1.2rem;
      border-radius: 0.2rem;
      background: rgba(var(--color-border, 235, 235, 235), var(--alpha-border, 1));
    }

    /*
      The fill and the cart's dot stop 2.2rem short of the rail's end, where the goal is
      centred: the dot, ring included, then stays clear of the goal's circle.
    */
    .fill,
    .dot {
      position: absolute;
      transition-duration: var(--duration-default, 300ms);
    }

    .fill {
      inset-block: 0;
      inset-inline-start: 0;
      width: min(var(--progress), calc(100% - 2.2rem));
      border-radius: 0.2rem;
      background: var(--line);
      transition-property: width;
    }

    .dot {
      top: 50%;
      inset-inline-start: min(var(--progress), calc(100% - 2.2rem));
      box-sizing: border-box;
      width: 1.4rem;
      height: 1.4rem;
      margin-block-start: -0.7rem;
      margin-inline-start: -0.7rem;
      border: 0.3rem solid var(--paper);
      border-radius: 50%;
      background: var(--line);
      box-shadow: 0 0 0 0.1rem var(--line);
      transition-property: inset-inline-start;
    }

    .goal {
      position: absolute;
      top: 0;
      inset-inline-end: 0;
      box-sizing: border-box;
      display: grid;
      place-items: center;
      width: 2.4rem;
      height: 2.4rem;
      border: 0.15rem solid var(--line);
      border-radius: 50%;
      background: var(--paper);
      color: var(--line);
    }

    .goal svg {
      width: 1.3rem;
      height: 1.3rem;
    }

    .tick,
    .unlocked .van,
    .unlocked .dot {
      display: none;
    }

    .unlocked .tick {
      display: block;
    }

    .unlocked .fill {
      width: 100%;
    }

    .unlocked .goal {
      background: var(--line);
      color: var(--paper);
    }

    /* On a narrow phone the goal's label drops below the cart's, still at the end. */
    .labels {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      gap: 0.4rem 1.2rem;
      margin-block-start: 0.2rem;
      font-size: 1.2rem;
      line-height: 1.3;
      color: rgba(var(--color-heading-text, 0, 0, 0), 0.6);
    }

    .labels span {
      white-space: nowrap;
    }

    .labels span + span {
      margin-inline-start: auto;
    }

    @media (prefers-reduced-motion: reduce) {
      .fill,
      .dot {
        transition: none;
      }
    }
  `;

  // Every bar on the page, wherever it is: UpCart's shadow root or the document.
  const bars = new Set();

  // The cart as last reported: { total, itemCount }, total in minor units. null until known.
  let cart = null;
  // Count the reports and the /cart.js reads, so an older read can't overwrite newer news.
  let reports = 0;
  let reads = 0;

  const drawAll = () => bars.forEach((bar) => bar.draw());

  function report(total, itemCount) {
    cart = { total, itemCount };
    reports += 1;
    drawAll();
  }

  // UpCart's cart events: { cart: { items, currency, total, totalBeforeDiscounts } }, where
  // total is total_price from UpCart's own /cart.js read. It has no item count.
  const onUpcartCart = ({ cart: upcart } = {}) => {
    // Should a change to UpCart drop the total, the cart is read instead.
    if (!Number.isFinite(upcart?.total) || !Array.isArray(upcart.items)) return readCart();
    report(upcart.total, upcart.items.reduce((count, item) => count + item.quantity, 0));
  };

  // For a bar that appears before UpCart has reported the cart, or a report without a total.
  // Only the latest read counts, and only if no report came in while it was on its way.
  function readCart() {
    const read = ++reads;
    const reportsBefore = reports;
    fetch('/cart.js')
      .then((response) => response.json())
      .then(({ total_price, item_count }) => {
        if (read === reads && reports === reportsBefore) report(total_price, item_count);
      })
      .catch((error) => console.error('Free shipping bar could not read the cart:', error));
  }

  // UpCart's API exists once its bundle has run. The bundle is deferred like this script but
  // comes later in the page, so subscribing is tried now, again once the document has loaded,
  // and whenever a bar appears: UpCart can only insert one when it's running.
  let subscribed = false;
  function subscribe() {
    if (subscribed || typeof window.upcartSubscribeCartUpdated !== 'function') return;
    subscribed = true;
    window.upcartSubscribeCartLoaded(onUpcartCart);
    window.upcartSubscribeCartUpdated(onUpcartCart);
    window.upcartSubscribeCartOpened(drawAll);
  }

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      if (!this.shadowRoot) {
        this.attachShadow({ mode: 'open' }).innerHTML = `
          <style>${STYLES}</style>
          <div class="bar">
            <p class="message"></p>
            <div class="track" aria-hidden="true">
              <div class="rail"><span class="fill"></span><span class="dot"></span></div>
              <span class="goal">${VAN}${TICK}</span>
            </div>
            <p class="labels"><span class="spend"></span><span class="target"></span></p>
          </div>
        `;
      }
      bars.add(this);
      subscribe();
      // The cart page's bar comes with the cart it was built with (snippets/free-shipping-bar.liquid),
      // fresh each time the section is re-rendered after a change. UpCart's bare element has none.
      if (this.dataset.cartTotal !== undefined) {
        report(Number(this.dataset.cartTotal), Number(this.dataset.itemCount));
      } else {
        this.draw();
      }
      if (!cart) readCart();
    }

    disconnectedCallback() {
      bars.delete(this);
    }

    // The maths of updateFreeShipping() in global.js, and its formatPrice().
    draw() {
      const {
        enable_shipping_notification: enabled,
        cart_free_shipping_threshold: threshold,
        cart_free_shipping_bar: words,
        money_format: moneyFormat,
      } = window.theme?.settings || {};

      this.hidden = !enabled || !threshold || !words || !cart || cart.itemCount === 0;
      if (this.hidden) return;

      const unlocked = cart.total >= threshold;
      // A function, not a replacement string: an amount can hold a $ ("$11.00").
      const withAmount = (text, cents) => text.replace('[amount]', () => formatPrice(moneyFormat, cents / 100));
      const bar = this.shadowRoot.querySelector('.bar');
      bar.classList.toggle('unlocked', unlocked);
      bar.style.setProperty('--progress', `${unlocked ? 100 : (cart.total / threshold) * 100}%`);
      bar.querySelector('.message').innerHTML = unlocked ? words.unlocked : withAmount(words.remaining, threshold - cart.total);
      bar.querySelector('.spend').innerHTML = withAmount(words.spend, cart.total);
      bar.querySelector('.target').innerHTML = words.goal;
    }
  }

  customElements.define('wtp-free-shipping-bar', FreeShippingBar);
  subscribe();
  document.addEventListener('DOMContentLoaded', subscribe);
})();
